/**
 * AssetBrowser Component - Character/Monster Asset Management for Battleroom
 * 
 * This component provides a browsable library of characters and monsters that users
 * can add as tokens to the battleroom. It replaces the problematic direct navigation
 * approach with a proper asset management system within the battleroom itself.
 * 
 * KEY FEATURES:
 * - Browse available characters and monsters from dashboard data
 * - Add tokens directly to the current battle session
 * - Search and filter functionality
 * - Preview character/monster stats before adding
 * - Compact, collapsible interface that doesn't interfere with battle flow
 * 
 * WORKFLOW:
 * 1. User opens Asset Browser from battleroom toolbar
 * 2. Browse characters (Fighter, Wizard, etc.) and monsters (Goblin, Orc, etc.)
 * 3. Select a character or monster tile to place it on the Token Layer
 * 4. Token appears on map, ready for combat use
 * 
 * INTEGRATION:
 * - Uses same character/monster data as Dashboard
 * - Calls createTokenFromDashboardData from useBattleRoom hook
 * - Respects current layer settings and battle state
 * - No navigation or persistence issues
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DEBUG_ENABLED } from '../../utils/DebugLogger';
import { RefreshCw, Search, X } from 'lucide-react';
import UnifiedDataManager from '../../services/UnifiedDataManager';
// Removed unused getCharacterAsset import; artwork is resolved via ArtworkUtils.getArtworkProps
import ImageWithFallback from '../ImageWithFallback';
import { resolveCharacterArtUrl, resolveMonsterArtUrl } from '../../services/ArtworkResolver';
import {
  filterCombatReadyAssets,
  resolveAssetDocument,
  resolveCampaignScope,
} from './assetLibraryPolicy';
// @date_of_change: 2025-10-07 - SINGLE SOURCE OF TRUTH for artwork paths
// @date_of_change: 2025-11-09 - Lint cleanup: remove unused extractCanonicalKey import (CI treat warnings as errors)
import './AssetBrowser.css';

// date_of_change: 2025-08-29
// REPLACED fragmented imports with UnifiedDataManager:
// - DataService, characterTemplates, monsterTemplates → UnifiedDataManager
// - AdditionalMonsters → UnifiedDataManager (loads from data files)  
// - customAssetManager → UnifiedDataManager
// - MonsterDataNormalizer → removed (no longer needed)

// @date_of_change: 2025-11-09 - Lint cleanup: removed unused CustomAssetManager import (integration handled via UnifiedDataManager)

const DEFAULT_MODAL_SIZE = { width: 1080, height: 760 };
const MIN_MODAL_SIZE = { width: 720, height: 520 };
const VIEWPORT_MARGIN = 24;
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[href]',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getAvailableModalWidth = () => Math.max(360, window.innerWidth - (VIEWPORT_MARGIN * 2));
const getAvailableModalHeight = () => Math.max(360, window.innerHeight - (VIEWPORT_MARGIN * 2));

const clampModalSize = (size) => {
  const maxWidth = getAvailableModalWidth();
  const maxHeight = getAvailableModalHeight();

  return {
    width: clamp(size.width, Math.min(MIN_MODAL_SIZE.width, maxWidth), maxWidth),
    height: clamp(size.height, Math.min(MIN_MODAL_SIZE.height, maxHeight), maxHeight)
  };
};

const clampModalPosition = (position, size) => {
  const boundedSize = clampModalSize(size);
  const maxX = Math.max(VIEWPORT_MARGIN, window.innerWidth - boundedSize.width - VIEWPORT_MARGIN);
  const maxY = Math.max(VIEWPORT_MARGIN, window.innerHeight - boundedSize.height - VIEWPORT_MARGIN);

  return {
    x: clamp(position.x, VIEWPORT_MARGIN, maxX),
    y: clamp(position.y, VIEWPORT_MARGIN, maxY)
  };
};

const getCenteredModalPosition = (size) => {
  const boundedSize = clampModalSize(size);
  return clampModalPosition({
    x: Math.round((window.innerWidth - boundedSize.width) / 2),
    y: Math.round((window.innerHeight - boundedSize.height) / 2)
  }, boundedSize);
};

// The whole tile is the action so mouse, touch, and keyboard users get the same
// compact interaction on both library tabs. date_of_change: 2026-08-12
const AssetTile = ({
  entity,
  assetType,
  artworkUrl,
  fallback,
  details,
  stats,
  scopeLabel,
  templateLabel,
  onSelect,
}) => (
  <button
    type="button"
    className={`asset-browser-card ${assetType}-card`}
    data-testid={`asset-browser-${assetType}-${entity.id}`}
    onClick={() => onSelect(entity)}
    aria-label={`Add ${entity.name} to the battle map`}
    title={`Add ${entity.name} to the battle map`}
  >
    <span className="asset-browser-image">
      <ImageWithFallback
        src={artworkUrl}
        alt={entity.name}
        className="object-cover"
        fallback={fallback}
      />
      {entity.source === 'custom' && (
        <span className="custom-asset-badge" title={`Custom uploaded ${assetType}`}>
          Custom
        </span>
      )}
      {entity.error && (
        <span className="error-asset-badge" title={`${entity.name} data needs attention`}>
          Issue
        </span>
      )}
    </span>
    <span className="asset-browser-info">
      <span className="asset-browser-name">
        {entity.name}
        {entity.source === 'custom' && <span className="custom-indicator"> (Custom Upload)</span>}
        {entity.source === 'template' && <span className="template-indicator"> (Template)</span>}
      </span>
      {scopeLabel && <span className="asset-browser-scope-badge">{scopeLabel}</span>}
      <span className="asset-browser-details">{details}</span>
      <span className="asset-browser-stats">{stats}</span>
      {templateLabel && <span className="asset-browser-template">Based on: {templateLabel}</span>}
    </span>
  </button>
);

const AssetBrowser = ({ 
  isOpen, 
  onClose, 
  onAddToken, 
  className = '',
  surface = 'default',
  activeCampaignId = null,
}) => {
  const [activeTab, setActiveTab] = useState('characters');
  const [searchTerm, setSearchTerm] = useState('');
  const [allCharacters, setAllCharacters] = useState({});
  const [allMonsters, setAllMonsters] = useState({});
  const [loading, setLoading] = useState(false);
  const [includeCampaignActors, setIncludeCampaignActors] = useState(true);

  // @date_of_change 2025-09-20 - Add drag and resize functionality
  const [size, setSize] = useState(() => clampModalSize(DEFAULT_MODAL_SIZE));
  const [position, setPosition] = useState(() => getCenteredModalPosition(DEFAULT_MODAL_SIZE));
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const loadAllAssetsRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const priorFocusRef = React.useRef(null);
  const isBattleSurface = surface === 'battle';

  // date_of_change: 2025-10-18 - Load entities from UnifiedDataManager (SSOT)
  useEffect(() => {
    let cancelled = false;
    
    const loadAllAssets = async () => {
      if (!isOpen || cancelled) return;
      
      try {
        setLoading(true);
        if (DEBUG_ENABLED) console.debug('[AssetBrowser] Loading entities from UnifiedDataManager...');

        const [charMap, monMap] = await Promise.all([
          UnifiedDataManager.getAllEntities('character'),
          UnifiedDataManager.getAllEntities('monster')
        ]);

        if (cancelled) return;

        const characters = {};
        Object.values(charMap || {}).forEach((entity) => {
          const document = resolveAssetDocument(entity);
          const d = document.data && typeof document.data === 'object' ? document.data : document;
          const disp = UnifiedDataManager.getDisplayData(document);
          const metadata = document._meta || entity._meta || {};
          const campaignScope = resolveCampaignScope(document);
          const canonicalTokenAssetKey = d.tokenAssetKey || d.tokenAsset?.key || entity._meta?.tokenAssetKey || entity._meta?.tokenAsset?.key;
          const normalizedTokenAssetKey = canonicalTokenAssetKey ? String(canonicalTokenAssetKey).replace(/^\/+/, '') : null;
          characters[entity.id] = {
            id: entity.id,
            name: disp.name || d.identity?.name || d.name || 'Unknown',
            class: disp.class || (Array.isArray(d.classes) && d.classes[0]?.name) || d.class || 'Unknown',
            level: disp.level,
            hp: disp.hp,
            maxHp: disp.maxHp,
            ac: disp.ac,
            type: 'Character',
            isCustom: !!entity.tags?.includes('custom'),
            source: 'unified',
            artwork: metadata.artwork,
            tokenAssetKey: normalizedTokenAssetKey || undefined,
            tokenAsset: normalizedTokenAssetKey ? { key: normalizedTokenAssetKey } : undefined,
            canonicalData: d,
            originalUnifiedEntity: document,
            campaignScope,
          };
        });

        const monsters = {};
        Object.values(monMap || {}).forEach((entity) => {
          const document = resolveAssetDocument(entity);
          const d = document.data && typeof document.data === 'object' ? document.data : document;
          const disp = UnifiedDataManager.getDisplayData({ ...document, type: 'monster', id: entity.id || document.id });
          const metadata = document._meta || entity._meta || {};
          const campaignScope = resolveCampaignScope(document);
          // Canonical challenge.cr must agree with the dashboard and sheet.
          // date_of_change: 2026-11-05
          const crDisplay = disp.cr;
          const canonicalTokenAssetKey = d.tokenAssetKey || d.tokenAsset?.key || entity._meta?.tokenAssetKey || entity._meta?.tokenAsset?.key;
          const normalizedTokenAssetKey = canonicalTokenAssetKey ? String(canonicalTokenAssetKey).replace(/^\/+/, '') : null;
          monsters[entity.id] = {
            id: entity.id,
            name: d.name || document.name || 'Unknown',
            cr: crDisplay,
            hp: disp.hp,
            maxHp: disp.maxHp,
            ac: disp.ac,
            type: 'Monster',
            isCustom: !!entity.tags?.includes('custom'),
            source: 'unified',
            artwork: metadata.artwork,
            tokenAssetKey: normalizedTokenAssetKey || undefined,
            tokenAsset: normalizedTokenAssetKey ? { key: normalizedTokenAssetKey } : undefined,
            canonicalData: d,
            originalUnifiedEntity: document,
            entityKind: d.entityKind || document.entityKind || 'monster',
            campaignScope,
          };
        });

        setAllCharacters(characters);
        setAllMonsters(monsters);
        
        if (DEBUG_ENABLED) console.debug('[AssetBrowser] Loaded entities:', {
          characterCount: Object.keys(characters).length,
          monsterCount: Object.keys(monsters).length
        });
        
      } catch (error) {
        if (!cancelled) {
          console.error('[AssetBrowser] Failed to load entities:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAllAssetsRef.current = loadAllAssets;

    if (isOpen) {
      loadAllAssets();
    }
    
    // Cleanup function to prevent calls after component unmounts or closes
    return () => {
      cancelled = true;
      if (DEBUG_ENABLED) console.debug('[AssetBrowser] useEffect cleanup - cancelling pending requests');
      loadAllAssetsRef.current = null;
    };
  }, [isOpen]); // Reload when browser opens to get latest data

  // @date_of_change 2025-09-20 - Add drag and resize event handlers
  const handleMouseDown = (e) => {
    if (!e.target.closest('.asset-browser-header') || e.target.closest('button, input')) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition(clampModalPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      }, size));
    } else if (isResizing) {
      const nextSize = clampModalSize({
        width: e.clientX - position.x,
        height: e.clientY - position.y
      });
      setSize(nextSize);
      setPosition((currentPosition) => clampModalPosition(currentPosition, nextSize));
    }
  }, [isDragging, isResizing, dragStart, position, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  // Add global event listeners for drag/resize
  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isOpen) return undefined;

    priorFocusRef.current = document.activeElement;

    const handleViewportResize = () => {
      setSize((currentSize) => {
        const nextSize = clampModalSize(currentSize);
        setPosition((currentPosition) => clampModalPosition(currentPosition, nextSize));
        return nextSize;
      });
    };

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusableControls = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusableControls.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstControl = focusableControls[0];
      const lastControl = focusableControls[focusableControls.length - 1];

      if (event.shiftKey && document.activeElement === firstControl) {
        event.preventDefault();
        lastControl.focus();
      } else if (!event.shiftKey && document.activeElement === lastControl) {
        event.preventDefault();
        firstControl.focus();
      }
    };

    const focusSearch = () => {
      dialogRef.current?.querySelector('[data-asset-browser-autofocus]')?.focus();
    };

    handleViewportResize();
    const focusTimer = window.setTimeout(focusSearch, 0);
    window.addEventListener('resize', handleViewportResize);
    document.addEventListener('keydown', handleDialogKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('keydown', handleDialogKeyDown);

      const priorFocus = priorFocusRef.current;
      if (priorFocus && typeof priorFocus.focus === 'function' && document.contains(priorFocus)) {
        priorFocus.focus();
      }
    };
  }, [isOpen, onClose]);

  
  const characters = Object.values(allCharacters).map(entity => {
    // @date_of_change 2025-09-08 - ARCHITECTURAL FIX: Use direct canonical data access
    // Following AGENTS.md rule: "ENFORCE zero transformation rule - canonical format throughout the entire pipeline"
    // Data should already be in CanonicalCharacter format from storage, no normalizer needed
    
    return {
      // Single source of truth - all data directly from entity (already canonical)
      ...entity,
      // Display metadata for AssetBrowser UI
      type: 'Character',
      isCustom: entity.source === 'custom',
      source: entity.source || 'template',
      baseTemplate: entity.source === 'custom' ? 'custom' : entity.id,
      // Artwork resolved at render time via resolveCharacterArtUrl
    };
  });

  const monsters = Object.values(allMonsters).map(entity => {
    // @date_of_change 2025-09-08 - ARCHITECTURAL FIX: Use direct canonical data access  
    // Following AGENTS.md rule: "ENFORCE zero transformation rule - canonical format throughout the entire pipeline"
    // Data should already be in CanonicalMonster format from storage, no normalizer needed
    
    return {
      // Single source of truth - all data directly from entity (already canonical)
      ...entity,
      // Display metadata for AssetBrowser UI
      type: 'Monster',
      isCustom: entity.source === 'custom',
      source: entity.source || 'template', 
      baseTemplate: entity.source === 'custom' ? 'custom' : entity.id,
      // Artwork resolved at render time via resolveMonsterArtUrl
    };
  });

  if (DEBUG_ENABLED) console.debug('[AssetBrowser] Prepared monsters for display:', monsters);

  // Filter function for search
  const filterEntities = (entities) => {
    // @date_of_change: 2025-11-13 - Null-safe search across possibly object-shaped fields
    if (!searchTerm) return entities;
    const needle = String(searchTerm || '').toLowerCase();
    return entities.filter(entity => {
      const nameStr = String(entity?.name || '').toLowerCase();
      const classStr = typeof entity?.class === 'string'
        ? entity.class.toLowerCase()
        : (entity?.class && typeof entity.class === 'object')
          ? JSON.stringify(entity.class).toLowerCase()
          : '';
      const crStr = (() => {
        const cr = entity?.cr;
        if (cr == null) return '';
        if (typeof cr === 'object') {
          const v = cr.display ?? cr.value ?? cr.average ?? (cr.formula ? cr.formula : '');
          return String(v).toLowerCase();
        }
        return String(cr).toLowerCase();
      })();
      return nameStr.includes(needle) || classStr.includes(needle) || crStr.includes(needle);
    });
  };

  const handleAddToken = (entityData) => {
    if (onAddToken) {
      // @date_of_change 2025-08-30 - EntityStorage provides consistent format
      if (DEBUG_ENABLED) console.debug('[AssetBrowser] Sending entity data to token creation:', {
        id: entityData.id,
        name: entityData.name,
        type: entityData.type,
        hasAbilities: !!entityData.abilities,
        actionCount: entityData.actions?.length || 0,
        traitCount: entityData.traits?.length || 0
      });
      
      onAddToken(entityData);
    }
  };

  const handleTabKeyDown = (event) => {
    const tabs = ['characters', 'monsters'];
    const currentIndex = tabs.indexOf(activeTab);
    let nextTab = null;

    if (event.key === 'ArrowRight') {
      nextTab = tabs[(currentIndex + 1) % tabs.length];
    } else if (event.key === 'ArrowLeft') {
      nextTab = tabs[(currentIndex - 1 + tabs.length) % tabs.length];
    } else if (event.key === 'Home') {
      nextTab = tabs[0];
    } else if (event.key === 'End') {
      nextTab = tabs[tabs.length - 1];
    }

    if (!nextTab) return;

    event.preventDefault();
    setActiveTab(nextTab);
    dialogRef.current?.querySelector(`[data-asset-browser-tab="${nextTab}"]`)?.focus();
  };

  if (!isOpen) return null;

  const characterPolicy = filterCombatReadyAssets(characters, {
    type: 'character', includeCampaignActors, activeCampaignId,
  });
  const monsterPolicy = filterCombatReadyAssets(monsters, {
    type: 'monster', includeCampaignActors, activeCampaignId,
  });
  const filteredCharacters = filterEntities(characterPolicy.visible);
  const filteredMonsters = filterEntities(monsterPolicy.visible);
  const visibleEntities = activeTab === 'characters' ? filteredCharacters : filteredMonsters;
  const activePolicy = activeTab === 'characters' ? characterPolicy : monsterPolicy;

  const modalContent = (
    <div
      className={`asset-browser-overlay ${isBattleSurface ? 'asset-browser-overlay--battle' : ''} ${className}`}
      data-testid="asset-browser-overlay"
    >
      <div
        ref={dialogRef}
        className={`asset-browser-modal ${isBattleSurface ? 'asset-browser-modal--battle' : ''}`}
        onMouseDown={handleMouseDown}
        data-testid="asset-browser-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="asset-browser-title"
        aria-describedby="asset-browser-subtitle"
        tabIndex={-1}
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        {/* Header */}
        <div
          className="asset-browser-header"
          style={{ cursor: 'grab' }}
        >
          <div className="asset-browser-title-group">
            <span className="asset-browser-eyebrow">Token library</span>
            <h2 id="asset-browser-title" className="asset-browser-title">Add Tokens</h2>
            <p id="asset-browser-subtitle" className="asset-browser-subtitle">
              Search your saved characters and monsters, then place them directly on the battle map.
            </p>
          </div>
          <div className="asset-browser-header-actions">
            <button 
              className="asset-browser-refresh"
              onClick={async () => {
                const loader = loadAllAssetsRef.current;
                if (!loader) return;
                try {
                  await loader();
                } catch (error) {
                  console.error('[AssetBrowser] Refresh failed:', error);
                }
              }}
              title="Refresh all entities"
              aria-label="Refresh all entities"
              disabled={loading}
            >
              <RefreshCw size={18} className={loading ? 'asset-browser-icon-spin' : ''} />
            </button>
            <button 
              className="asset-browser-close"
              onClick={onClose}
              title="Close Add Tokens"
              aria-label="Close Add Tokens"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="asset-browser-search">
          <div className="asset-browser-search-row">
            <div className="asset-browser-search-field">
              <Search size={18} className="asset-browser-search-icon" />
              <input
                id="asset-browser-search-input"
                type="text"
                placeholder="Search characters and monsters"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="asset-browser-search-input"
                aria-label="Search characters and monsters"
                data-testid="asset-browser-search-input"
                data-asset-browser-autofocus
              />
              {searchTerm && (
                <button
                  type="button"
                  className="asset-browser-search-clear"
                  onClick={() => setSearchTerm('')}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="asset-browser-search-meta">
              {loading ? 'Refreshing...' : `${visibleEntities.length} results`}
            </div>
            <button
              type="button"
              className={`asset-browser-campaign-toggle ${includeCampaignActors ? 'is-active' : ''}`}
              role="switch"
              aria-checked={includeCampaignActors}
              onClick={() => setIncludeCampaignActors((included) => !included)}
              title={includeCampaignActors ? 'Hide campaign-specific actors' : 'Include campaign-specific actors'}
            >
              <span className="asset-browser-toggle-track" aria-hidden="true">
                <span className="asset-browser-toggle-knob" />
              </span>
              <span className="asset-browser-toggle-copy">
                <strong>Campaign actors</strong>
                <small>{includeCampaignActors ? 'Included' : 'Excluded'}</small>
              </span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="asset-browser-tabs" role="tablist" aria-label="Token library types">
          <button
            type="button"
            className={`asset-browser-tab ${activeTab === 'characters' ? 'active' : ''}`}
            onClick={() => setActiveTab('characters')}
            data-testid="asset-browser-tab-characters"
            data-asset-browser-tab="characters"
            id="asset-browser-tab-characters"
            role="tab"
            aria-selected={activeTab === 'characters'}
            aria-controls="asset-browser-panel-characters"
            tabIndex={activeTab === 'characters' ? 0 : -1}
            onKeyDown={handleTabKeyDown}
          >
            Characters ({filteredCharacters.length})
          </button>
          <button
            type="button"
            className={`asset-browser-tab ${activeTab === 'monsters' ? 'active' : ''}`}
            onClick={() => setActiveTab('monsters')}
            data-testid="asset-browser-tab-monsters"
            data-asset-browser-tab="monsters"
            id="asset-browser-tab-monsters"
            role="tab"
            aria-selected={activeTab === 'monsters'}
            aria-controls="asset-browser-panel-monsters"
            tabIndex={activeTab === 'monsters' ? 0 : -1}
            onKeyDown={handleTabKeyDown}
          >
            NPCs &amp; Monsters ({filteredMonsters.length})
          </button>
        </div>

        {/* Content */}
        <div
          className="asset-browser-content"
          id={`asset-browser-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`asset-browser-tab-${activeTab}`}
        >
          {loading && (
            <div className="asset-browser-loading">
              <div className="loading-spinner"></div>
              <p>Loading entities...</p>
            </div>
          )}
          
          {!loading && activeTab === 'characters' && (
            <div className="asset-browser-grid">
              {filteredCharacters.map(character => {
                const url = resolveCharacterArtUrl(character);
                if (DEBUG_ENABLED) console.debug('[art-url][character]', character?.name || character?.id, url);
                return (
                <AssetTile
                  key={character.id}
                  entity={character}
                  assetType="character"
                  artworkUrl={url}
                  fallback="character-default"
                  scopeLabel={character.campaignScope?.campaignSpecific ? 'Campaign character' : null}
                  details={(
                    <>
                      {typeof character.class === 'object' ? JSON.stringify(character.class) : (character.class ?? 'Unknown')} • Level {(character && character.level && typeof character.level === 'object') ? (character.level.value ?? character.level.display ?? '—') : (character?.level ?? '—')}
                    </>
                  )}
                  stats={(
                    <>
                      HP: {character && character.hp && typeof character.hp === 'object' ? (character.hp.average ?? character.hp.value ?? '—') : (character?.hp ?? '—')}/
                         {character && character.maxHp && typeof character.maxHp === 'object' ? (character.maxHp.average ?? character.maxHp.value ?? '—') : (character?.maxHp ?? '—')} • 
                      AC: {character && character.ac && typeof character.ac === 'object' ? (character.ac.value ?? character.ac.base ?? character.ac ?? '—') : (character?.ac ?? '—')}
                    </>
                  )}
                  templateLabel={character.isCustom && character.baseTemplate
                    ? character.baseTemplate.charAt(0).toUpperCase() + character.baseTemplate.slice(1)
                    : null}
                  onSelect={handleAddToken}
                />
              );})}
            </div>
          )}

          {!loading && activeTab === 'monsters' && (
            <div className="asset-browser-grid">
              {filteredMonsters.map(monster => {
                const url = resolveMonsterArtUrl(monster);
                if (DEBUG_ENABLED) console.debug('[art-url][monster]', monster?.name || monster?.id, url);
                return (
                <AssetTile
                  key={monster.id}
                  entity={monster}
                  assetType="monster"
                  artworkUrl={url}
                  fallback="monster-default"
                  scopeLabel={monster.campaignScope?.campaignSpecific
                    ? `Campaign ${String(monster.entityKind).toLowerCase() === 'npc' ? 'NPC' : 'monster'}`
                    : null}
                  details={(
                    <>
                      {(monster && monster.creatureType && typeof monster.creatureType === 'object') ? 
                        (monster.creatureType.type || monster.creatureType.name || 'Unknown') : 
                        (monster?.creatureType || monster?.type || 'Monster')} • CR {monster.cr}
                    </>
                  )}
                  stats={(
                    <>
                      HP: {monster && monster.hp && typeof monster.hp === 'object' ? (monster.hp.average ?? monster.hp.value ?? '—') : (monster?.hp ?? '—')}/
                         {monster && monster.maxHp && typeof monster.maxHp === 'object' ? (monster.maxHp.average ?? monster.maxHp.value ?? '—') : (monster?.maxHp ?? '—')} • 
                      AC: {monster && monster.ac && typeof monster.ac === 'object' ? (monster.ac.value ?? monster.ac.base ?? monster.ac ?? '—') : (monster?.ac ?? '—')}
                    </>
                  )}
                  onSelect={handleAddToken}
                />
              );})}
            </div>
          )}

          {!loading && visibleEntities.length === 0 && (
            <div className="asset-browser-empty">
              <h3>No matching {activeTab}</h3>
              <p>Try a different name, class, or CR to find the token you want.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="asset-browser-footer">
          <p className="asset-browser-help">
            <span className="asset-browser-help-lead">Select a combat-ready character, NPC, or monster to add to the battle map.</span>
            {activePolicy.incompleteCount > 0 && (
              <span className="asset-browser-help-status">
                {activePolicy.incompleteCount} incomplete record{activePolicy.incompleteCount === 1 ? ' is' : 's are'} hidden.
              </span>
            )}
            {activePolicy.otherCampaignCount > 0 && (
              <span className="asset-browser-help-status">
                {activePolicy.otherCampaignCount} actor{activePolicy.otherCampaignCount === 1 ? '' : 's'} from other campaigns hidden.
              </span>
            )}
          </p>
        </div>

        {/* Resize Handle */}
        <div
          className="asset-browser-resize-handle"
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 20,
            height: 20,
            cursor: 'se-resize',
            background: isBattleSurface
              ? 'linear-gradient(-45deg, transparent 0%, transparent 40%, rgba(199, 169, 107, 0.72) 40%, rgba(199, 169, 107, 0.72) 60%, transparent 60%)'
              : 'linear-gradient(-45deg, transparent 0%, transparent 40%, #ccc 40%, #ccc 60%, transparent 60%)',
            zIndex: 1001
          }}
        />
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default AssetBrowser;
